// MapLibre canvas, driven declaratively from props.
//
// One component serves the public live map, the trip journey replay, and any
// future map surface: the difference is only which data it is handed.
// Everything MapLibre touches is imperative and lives here, so no other
// component ever imports the library.
//
// MARKERS ARE DOM, NOT A SYMBOL LAYER. Live travellers render as avatar
// faces, which are SVG built by a React component, so they must be real HTML
// markers. The pattern used here is: MapLibre owns an empty element per
// cluster and positions it, React owns what is inside it through a portal.
// That keeps avatar rendering declarative without a second React root.
//
// PINS ONLY, NO LINES (round 6). There is no polyline on any map surface in
// this product: no GPS trail, no journey replay route, no connector between
// related pins. The line came from the round 1 reading of "realtime", which
// round 3 corrected: a new diary publishes a new PIN, it does not trace a
// moving dot, so a line drew the eye to data the product no longer centres
// and competed with the pins that are the actual content. The track module
// still ingests, stores and serves its points, and the trip screen still
// reports them as numbers. This component simply does not know about them,
// which is why it has no `track` prop: no dead source, no dead layer, and
// nothing recomputing a geometry nobody draws. The only remaining line layer
// is the hairline edge of the GPS accuracy circle, which is the boundary of
// an area, not a route.
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  INDONESIA_BOUNDS,
  INDONESIA_FIT_PADDING,
  INDONESIA_MIN_ZOOM,
  INDONESIA_PAN_MARGIN_DEG,
} from "../lib/config";
import {
  loadMapLibre,
  styleFor,
  terrainSourceSpec,
  MAP_ATTRIBUTION,
  TERRAIN_SOURCE_ID,
  type Basemap,
  type MapInstance,
  type MapLibreModule,
} from "../lib/maplibre";
import {
  clusterByScreen,
  AVATAR_CLUSTER_PX,
  MOMENT_CLUSTER_PX,
  type Cluster,
  type ClusterPoint,
} from "../lib/cluster";
import { PlaceMarker, PresenceMarker } from "./MapMarkers";
import { travellersOfPlaces } from "../lib/places";
import type { LivePresence, Place } from "../lib/types";
export type MapDimension = "2d" | "3d";

const SRC_ACCURACY = "traveldiary-accuracy";
const LAYER_ACCURACY_FILL = "traveldiary-accuracy-fill";
const LAYER_ACCURACY_EDGE = "traveldiary-accuracy-edge";

/** Marker move animation. Transform only, and under the 300ms ceiling. */
const MOVE_MS = 240;
/** Past this projected distance a move is a teleport, so it jumps instead. */
const MOVE_MAX_PX = 400;

export interface UserFix {
  lat: number;
  lng: number;
  accuracyM: number | null;
}

export interface MapLabels {
  /** Own pin, e.g. "You are here". */
  self: string;
  /** Cluster of live travellers, e.g. "Travellers here". */
  travellers: string;
  /** A destination pin, e.g. "Diaries here". */
  place: string;
}

export interface MapViewProps {
  basemap: Basemap;
  dimension: MapDimension;
  /**
   * The map's PRIMARY layer: one pin per destination that has public diary
   * entries. A new entry becomes a new pin with no reload.
   */
  places?: Place[];
  /**
   * Secondary layer, off by default. Live presence is kept and works, but it
   * is not what the map is about.
   */
  presence?: LivePresence[];
  selfUserId?: string | null;
  user: UserFix | null;
  follow: boolean;
  /** Bumping this recenters on the user, or fits the data when there is none. */
  recenterNonce: number;
  /**
   * Frame the data on first paint instead of the country. The trip replay
   * wants this; the public map does not, because it must open on Indonesia.
   */
  fitToData?: boolean;
  /** Bumping `nonce` flies to the coordinate, used to focus one pin. */
  focus?: { lat: number; lng: number; nonce: number } | null;
  /**
   * Pick mode: a tap on the map reports a coordinate instead of doing
   * nothing. Used by the write later flow, where the traveller drops a pin
   * for a place they are no longer standing in.
   */
  pickMode?: boolean;
  pickedPoint?: { lat: number; lng: number } | null;
  onPickPoint?(point: { lat: number; lng: number }): void;
  labels?: MapLabels;
  onSelectPlaces?(cluster: Cluster<Place>): void;
  onSelectTravellers?(cluster: Cluster<LivePresence>): void;
  onError(message: string): void;
}

const DEFAULT_LABELS: MapLabels = {
  self: "You",
  travellers: "Travellers",
  place: "Diaries",
};

interface MarkerEntry {
  marker: { setLngLat(coords: [number, number]): unknown; remove(): unknown };
  element: HTMLElement;
  lng: number;
  lat: number;
  raf: number | null;
}

function emptyPolygon() {
  return {
    type: "Feature" as const,
    properties: {},
    geometry: { type: "Polygon" as const, coordinates: [[]] as number[][][] },
  };
}

/** Approximate a circle of `radiusM` around a point as a 48 sided polygon. */
function circlePolygon(lat: number, lng: number, radiusM: number) {
  const coordinates: number[][] = [];
  const latRadius = radiusM / 111320;
  const lngRadius = radiusM / (111320 * Math.max(0.01, Math.cos((lat * Math.PI) / 180)));
  for (let i = 0; i <= 48; i += 1) {
    const angle = (i / 48) * 2 * Math.PI;
    coordinates.push([lng + lngRadius * Math.cos(angle), lat + latRadius * Math.sin(angle)]);
  }
  return {
    type: "Feature" as const,
    properties: {},
    geometry: { type: "Polygon" as const, coordinates: [coordinates] },
  };
}

/** Product scope: the map never leaves Indonesia, so nor does the camera. */
function insideIndonesia([lng, lat]: [number, number]): boolean {
  const [west, south, east, north] = INDONESIA_BOUNDS;
  return lng >= west && lng <= east && lat >= south && lat <= north;
}

function signatureOf<T>(clusters: Array<Cluster<T>>): string {
  return clusters
    .map(
      (cluster) =>
        `${cluster.id}@${cluster.lng.toFixed(6)},${cluster.lat.toFixed(6)}:${cluster.members
          .map((member) => member.id)
          .join(",")}`,
    )
    .join("|");
}

export function MapView(props: MapViewProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<MapInstance | null>(null);
  const glRef = useRef<MapLibreModule | null>(null);
  const readyRef = useRef(false);
  const userMarkerRef = useRef<{ setLngLat(c: [number, number]): unknown; remove(): unknown } | null>(
    null,
  );
  const pickMarkerRef = useRef<{ setLngLat(c: [number, number]): unknown; remove(): unknown } | null>(
    null,
  );
  const presenceMarkersRef = useRef(new Map<string, MarkerEntry>());
  const placeMarkersRef = useRef(new Map<string, MarkerEntry>());
  const presenceSigRef = useRef("");
  const placeSigRef = useRef("");
  const basemapRef = useRef<Basemap>(props.basemap);
  const didInitialFitRef = useRef(false);
  const didCountryFitRef = useRef(false);
  const recomputeRafRef = useRef<number | null>(null);
  // A focus request can arrive before MapLibre has finished loading (opening
  // the map from an Explore row does exactly that). Hold it and apply it once
  // the style is up, otherwise the fly is silently dropped.
  const pendingFocusRef = useRef<{ lat: number; lng: number } | null>(null);
  const propsRef = useRef(props);
  propsRef.current = props;

  const [presenceClusters, setPresenceClusters] = useState<Array<Cluster<LivePresence>>>([]);
  const [placeClusters, setPlaceClusters] = useState<Array<Cluster<Place>>>([]);

  const labels = props.labels ?? DEFAULT_LABELS;

  // Create the map once. MapLibre is loaded from our own origin at runtime,
  // see src/lib/maplibre.ts for why it is not bundled.
  useEffect(() => {
    let disposed = false;

    void loadMapLibre()
      .then((gl) => {
        if (disposed || !containerRef.current) return;
        glRef.current = gl;
        const map = new gl.Map({
          container: containerRef.current,
          style: styleFor(propsRef.current.basemap) as never,
          // Indonesia only, a product decision (see INDONESIA_BOUNDS). The
          // camera opens fitted to the country rather than on a hardcoded
          // centre and zoom, so it frames correctly at every viewport size,
          // and it cannot be panned or zoomed out of the country.
          bounds: INDONESIA_BOUNDS as never,
          fitBoundsOptions: { padding: INDONESIA_FIT_PADDING },
          minZoom: INDONESIA_MIN_ZOOM,
          // Rotation is off: a tilted or rotated country is disorienting and
          // it also breaks the "north is up" reading of the pan walls.
          dragRotate: false,
          pitch: propsRef.current.dimension === "3d" ? 60 : 0,
          // Off here, then added explicitly below. The constructor option
          // always mounts bottom right and takes no position, so moving it
          // means opting out and re-adding it.
          attributionControl: false,
        });
        // Attribution is licence required and must never be hidden. It lives
        // bottom LEFT because the capture control owns the bottom right, and
        // stacking them made that corner read as a pile of unrelated objects.
        // Both now sit on one baseline, see the .map-stage inset system in
        // styles.css.
        //
        // customAttribution is not decoration. Adding the control here races
        // the style load, and a control added before its sources exist renders
        // with `maplibregl-attrib-empty` and shows NOTHING. Caught exactly
        // that. Passing our own string means the licence text is present from
        // the first frame no matter the timing, and the style's own
        // attribution merges in beside it once the sources resolve. It also
        // covers the case where a future tile source ships no attribution
        // field at all.
        map.addControl(
          new gl.AttributionControl({ compact: true, customAttribution: MAP_ATTRIBUTION }),
          "bottom-left",
        );
        // Collapse it. `compact: true` renders a <details> that MapLibre
        // leaves `open`, so on a 375px phone three lines of attribution sit
        // permanently across the bottom of the map and collide with anything
        // above them. Measured it at 84px tall. Removing `open` gives the
        // small toggle button that compact mode is for.
        //
        // This does NOT hide the attribution, which would be a licence
        // violation: the text is one tap away, always present in the DOM, and
        // the control itself is untouched. Collapsing is the behaviour the
        // option already promises.
        const attribution = map.getContainer().querySelector("details.maplibregl-ctrl-attrib");
        attribution?.removeAttribute("open");
        mapRef.current = map;
        map.on("style.load", () => {
          readyRef.current = true;
          applyStyleScaffold();
          applyData();
        });
        // Clustering is screen space, so it has to be recomputed whenever the
        // projection changes. This is exactly what makes a cluster break
        // apart when you zoom in.
        map.on("move", scheduleRecompute);
        map.on("zoom", scheduleRecompute);
        // Indonesia has to FILL the viewport at maximum zoom out, at every
        // window size, so the floor is derived from the country's own fitted
        // zoom and re-derived whenever the viewport changes.
        map.on("resize", lockZoomToIndonesia);
        lockZoomToIndonesia();
        map.on("click", (event: { lngLat: { lat: number; lng: number } }) => {
          if (!propsRef.current.pickMode) return;
          propsRef.current.onPickPoint?.({ lat: event.lngLat.lat, lng: event.lngLat.lng });
        });
        map.on("error", () => {
          // Tile fetch failures are transient and MapLibre already retries,
          // do not surface them as a hard error over the whole screen.
        });
      })
      .catch(() => propsRef.current.onError("load-failed"));

    return () => {
      disposed = true;
      if (recomputeRafRef.current !== null) cancelAnimationFrame(recomputeRafRef.current);
      recomputeRafRef.current = null;
      for (const store of [presenceMarkersRef.current, placeMarkersRef.current]) {
        for (const entry of store.values()) {
          if (entry.raf !== null) cancelAnimationFrame(entry.raf);
          entry.marker.remove();
        }
        store.clear();
      }
      userMarkerRef.current?.remove();
      userMarkerRef.current = null;
      pickMarkerRef.current?.remove();
      pickMarkerRef.current = null;
      // A map that never finished initialising, which is what happens when
      // the device has no WebGL2, leaves MapLibre's painter undefined and
      // makes its own remove() throw. That throw happens inside a React
      // cleanup, so it unmounts the whole app and the traveller gets a blank
      // screen the moment they navigate away from the map. Teardown is best
      // effort by definition: the component is going away either way.
      try {
        mapRef.current?.remove();
      } catch {
        // Nothing to clean up beyond what has already been released above.
      }
      mapRef.current = null;
      readyRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Lock the camera to Indonesia at the CURRENT viewport size.
   *
   * "Only Indonesia" means the country FILLS the frame, so the floor is a
   * COVER fit, not a contain fit: contain would fit the country's 46 degrees
   * of longitude into a portrait phone and leave the Philippines, Vietnam and
   * Australia on screen, which is the "terlalu luas" complaint. Cover fills
   * the shorter axis with the country and the traveller pans along the
   * archipelago, hitting a wall at each end.
   *
   * Both numbers are derived, never hardcoded: a floor that is right on a
   * 375px phone is wrong on a 1280px desktop.
   */
  function lockZoomToIndonesia(): void {
    const map = mapRef.current;
    if (!map) return;
    try {
      map.setMaxBounds(null as never);
      const canvas = map.getCanvas();
      const width = canvas.clientWidth || 375;
      const height = canvas.clientHeight || 640;
      const [west, south, east, north] = INDONESIA_BOUNDS;

      // MapLibre's world is 512 px square at zoom 0.
      const TILE = 512;
      const mercatorY = (lat: number) => {
        const clamped = Math.max(-85.05, Math.min(85.05, lat));
        const radians = (clamped * Math.PI) / 180;
        return 0.5 - Math.log(Math.tan(Math.PI / 4 + radians / 2)) / (2 * Math.PI);
      };
      const lngSpan = Math.abs(east - west);
      const ySpan = Math.abs(mercatorY(north) - mercatorY(south));
      const zoomForWidth = Math.log2((360 * width) / (TILE * lngSpan));
      const zoomForHeight = Math.log2(height / (TILE * ySpan));
      // Cover: the larger of the two fills both axes with the country.
      const floor = Math.max(INDONESIA_MIN_ZOOM, Math.max(zoomForWidth, zoomForHeight));
      if (!Number.isFinite(floor)) return;

      map.setMinZoom(floor);

      const previous = {
        center: map.getCenter(),
        zoom: map.getZoom(),
        bearing: map.getBearing(),
        pitch: map.getPitch(),
      };
      // Someone reading the map at street level keeps their view; a camera
      // that was already showing the country floor gets reframed.
      const reframe = !didCountryFitRef.current || previous.zoom <= floor + 0.01;
      const center = { lng: (west + east) / 2, lat: (south + north) / 2 };

      map.jumpTo({ center: center as never, zoom: floor });
      const view = map.getBounds();
      const margin = INDONESIA_PAN_MARGIN_DEG;
      // The pan wall: the country plus whatever the floor framing already
      // shows, plus a small margin so an edge pin stays reachable.
      map.setMaxBounds([
        [Math.min(view.getWest(), west) - margin, Math.min(view.getSouth(), south) - margin],
        [Math.max(view.getEast(), east) + margin, Math.max(view.getNorth(), north) + margin],
      ] as never);
      if (!reframe) map.jumpTo(previous as never);
      didCountryFitRef.current = true;
    } catch {
      // Camera maths unavailable on this style state: the constant floor in
      // the map options still applies.
    }
  }

  function applyStyleScaffold(): void {
    const map = mapRef.current;
    if (!map) return;

    readyRef.current = true;

    if (!map.getSource(SRC_ACCURACY)) {
      map.addSource(SRC_ACCURACY, { type: "geojson", data: emptyPolygon() as never });
      map.addLayer({
        id: LAYER_ACCURACY_FILL,
        type: "fill",
        source: SRC_ACCURACY,
        paint: { "fill-color": "#16150f", "fill-opacity": 0.08 },
      });
      map.addLayer({
        id: LAYER_ACCURACY_EDGE,
        type: "line",
        source: SRC_ACCURACY,
        paint: { "line-color": "#6b675d", "line-width": 1 },
      });
    }

    applyTerrain();
  }

  function applyTerrain(): void {
    const map = mapRef.current;
    // Terrain is attached to the CURRENT style. Touching it while a style
    // swap is in flight throws inside MapLibre's render loop, so only ever
    // do it once style.load has fired.
    if (!map || !readyRef.current) return;
    const wants3d = propsRef.current.dimension === "3d";
    try {
      if (wants3d) {
        if (!map.getSource(TERRAIN_SOURCE_ID)) {
          map.addSource(TERRAIN_SOURCE_ID, terrainSourceSpec() as never);
        }
        map.setTerrain({ source: TERRAIN_SOURCE_ID, exaggeration: 1.2 });
      } else {
        map.setTerrain(null);
      }
    } catch {
      // Elevation tiles unreachable: the tilted view still works without
      // relief, so degrade quietly rather than break the screen.
    }
  }

  function applyData(): void {
    const map = mapRef.current;
    const gl = glRef.current;
    if (!map || !gl || !readyRef.current) return;
    const current = propsRef.current;

    const accuracySource = map.getSource(SRC_ACCURACY) as
      | { setData?(data: unknown): void }
      | undefined;
    if (accuracySource?.setData) {
      accuracySource.setData(
        current.user && current.user.accuracyM
          ? circlePolygon(current.user.lat, current.user.lng, current.user.accuracyM)
          : emptyPolygon(),
      );
    }

    syncUserMarker(gl, map, current.user);
    syncPickMarker(gl, map, current.pickedPoint ?? null);
    if (pendingFocusRef.current) {
      const pending = pendingFocusRef.current;
      pendingFocusRef.current = null;
      didInitialFitRef.current = true;
      map.jumpTo({ center: [pending.lng, pending.lat], zoom: Math.max(map.getZoom(), 15) });
    } else {
      fitToDataOnce(gl, map);
    }
    recomputeClusters();
  }

  /**
   * First paint only: frame whatever the map already has so it does not open
   * on an arbitrary default while every pin sits off screen. Later updates
   * leave the camera alone, moving it under someone reading the map is worse
   * than a slightly stale view.
   */
  function fitToDataOnce(gl: MapLibreModule, map: MapInstance): void {
    if (didInitialFitRef.current) return;
    const current = propsRef.current;
    // The public map opens on Indonesia, framed by the map's own `bounds`
    // option. Only a surface that asked for it reframes onto its own data.
    if (!current.fitToData) {
      didInitialFitRef.current = true;
      return;
    }
    if (current.user) {
      didInitialFitRef.current = true;
      map.jumpTo({ center: [current.user.lng, current.user.lat], zoom: 15 });
      return;
    }
    const coordinates: Array<[number, number]> = [
      ...(current.places ?? []).map((place) => [place.lng, place.lat] as [number, number]),
      ...(current.presence ?? []).map((live) => [live.lng, live.lat] as [number, number]),
    ];
    // One stray coordinate must not yank the camera out of the country.
    const inside = coordinates.filter(insideIndonesia);
    if (inside.length === 0) return;
    didInitialFitRef.current = true;
    const bounds = new gl.LngLatBounds(inside[0]!, inside[0]!);
    for (const coordinate of inside) bounds.extend(coordinate);
    map.fitBounds(bounds, { padding: 64, maxZoom: 15, duration: 0 });
  }

  function scheduleRecompute(): void {
    if (recomputeRafRef.current !== null) return;
    recomputeRafRef.current = requestAnimationFrame(() => {
      recomputeRafRef.current = null;
      recomputeClusters();
    });
  }

  function recomputeClusters(): void {
    const map = mapRef.current;
    const gl = glRef.current;
    if (!map || !gl || !readyRef.current) return;
    const current = propsRef.current;

    // Most recently seen first: the leader anchors the cluster and paints in
    // front of the rest of the stack.
    const presencePoints: Array<ClusterPoint<LivePresence>> = [...(current.presence ?? [])]
      .sort((a, b) => b.lastSeenAt.localeCompare(a.lastSeenAt))
      .map((live) => ({ id: live.user.id, lng: live.lng, lat: live.lat, item: live }));

    const placePoints: Array<ClusterPoint<Place>> = [...(current.places ?? [])]
      .sort((a, b) => b.lastMomentAt.localeCompare(a.lastMomentAt))
      .map((place) => ({ id: place.id, lng: place.lng, lat: place.lat, item: place }));

    const project = (point: { lng: number; lat: number }) => map.project([point.lng, point.lat]);

    const nextPresence = clusterByScreen(presencePoints, project, AVATAR_CLUSTER_PX);
    const nextPlaces = clusterByScreen(placePoints, project, AVATAR_CLUSTER_PX);

    const presenceSig = signatureOf(nextPresence);
    if (presenceSig !== presenceSigRef.current) {
      presenceSigRef.current = presenceSig;
      // Markers are created BEFORE the state update so the portals below
      // always have a live element to render into on the very next render.
      reconcileMarkers(gl, map, presenceMarkersRef.current, nextPresence, "live-marker");
      setPresenceClusters(nextPresence);
    }

    const placeSig = signatureOf(nextPlaces);
    if (placeSig !== placeSigRef.current) {
      placeSigRef.current = placeSig;
      reconcileMarkers(gl, map, placeMarkersRef.current, nextPlaces, "place-marker");
      setPlaceClusters(nextPlaces);
    }
  }

  function reconcileMarkers<T>(
    gl: MapLibreModule,
    map: MapInstance,
    store: Map<string, MarkerEntry>,
    clusters: Array<Cluster<T>>,
    className: string,
  ): void {
    const seen = new Set<string>();
    for (const cluster of clusters) {
      seen.add(cluster.id);
      const existing = store.get(cluster.id);
      if (existing) {
        moveMarker(map, existing, cluster.lng, cluster.lat);
        continue;
      }
      const element = document.createElement("div");
      element.className = className;
      const marker = new gl.Marker({ element, anchor: "center" })
        .setLngLat([cluster.lng, cluster.lat])
        .addTo(map);
      store.set(cluster.id, { marker, element, lng: cluster.lng, lat: cluster.lat, raf: null });
    }
    for (const [id, entry] of store) {
      if (seen.has(id)) continue;
      if (entry.raf !== null) cancelAnimationFrame(entry.raf);
      entry.marker.remove();
      store.delete(id);
    }
  }

  /**
   * Animate a marker to its new coordinate. MapLibre writes the marker's
   * position as a CSS `transform: translate`, so interpolating the coordinate
   * per frame is a transform only animation. It is done here rather than with
   * a CSS `transition: transform` on purpose: MapLibre rewrites that same
   * transform on every frame of a pan, and a transition on it would make the
   * whole marker layer lag behind the basemap while dragging.
   */
  function moveMarker(map: MapInstance, entry: MarkerEntry, lng: number, lat: number): void {
    if (entry.lng === lng && entry.lat === lat) return;
    if (entry.raf !== null) {
      cancelAnimationFrame(entry.raf);
      entry.raf = null;
    }
    const from = map.project([entry.lng, entry.lat]);
    const to = map.project([lng, lat]);
    if (Math.hypot(to.x - from.x, to.y - from.y) > MOVE_MAX_PX) {
      entry.marker.setLngLat([lng, lat]);
      entry.lng = lng;
      entry.lat = lat;
      return;
    }
    const fromLng = entry.lng;
    const fromLat = entry.lat;
    const start = performance.now();
    const step = (now: number) => {
      const t = Math.min(1, (now - start) / MOVE_MS);
      const eased = 1 - (1 - t) ** 3;
      const nextLng = fromLng + (lng - fromLng) * eased;
      const nextLat = fromLat + (lat - fromLat) * eased;
      entry.marker.setLngLat([nextLng, nextLat]);
      entry.lng = nextLng;
      entry.lat = nextLat;
      if (t < 1) {
        entry.raf = requestAnimationFrame(step);
        return;
      }
      entry.raf = null;
      entry.lng = lng;
      entry.lat = lat;
    };
    entry.raf = requestAnimationFrame(step);
  }

  /** The dropped pin in pick mode. A plain mark, never a traveller face. */
  function syncPickMarker(
    gl: MapLibreModule,
    map: MapInstance,
    point: { lat: number; lng: number } | null,
  ): void {
    if (!point) {
      pickMarkerRef.current?.remove();
      pickMarkerRef.current = null;
      return;
    }
    if (!pickMarkerRef.current) {
      const element = document.createElement("div");
      element.className = "map-pick";
      pickMarkerRef.current = new gl.Marker({ element, anchor: "center" })
        .setLngLat([point.lng, point.lat])
        .addTo(map);
      return;
    }
    pickMarkerRef.current.setLngLat([point.lng, point.lat]);
  }

  function syncUserMarker(gl: MapLibreModule, map: MapInstance, user: UserFix | null): void {
    if (!user) {
      userMarkerRef.current?.remove();
      userMarkerRef.current = null;
      return;
    }
    if (!userMarkerRef.current) {
      const element = document.createElement("div");
      element.className = "map-me";
      userMarkerRef.current = new gl.Marker({ element }).setLngLat([user.lng, user.lat]).addTo(map);
      return;
    }
    userMarkerRef.current.setLngLat([user.lng, user.lat]);
  }

  // Basemap switch: setStyle wipes every source and layer, so the scaffold
  // and the data are reapplied on the next style.load.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || basemapRef.current === props.basemap) return;
    basemapRef.current = props.basemap;
    readyRef.current = false;
    // setStyle drops every source, including the elevation source terrain
    // points at. Detach terrain first or MapLibre renders against a source
    // that no longer exists.
    try {
      map.setTerrain(null);
    } catch {
      // No terrain was set, nothing to detach.
    }
    map.setStyle(styleFor(props.basemap) as never);
  }, [props.basemap]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    applyTerrain();
    map.easeTo({ pitch: props.dimension === "3d" ? 60 : 0, duration: 400 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.dimension]);

  useEffect(() => {
    applyData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.places, props.presence, props.user, props.pickedPoint]);

  // Follow mode keeps the live position centred without fighting the user:
  // it only re-centres when a new fix arrives, not on every render.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !props.follow || !props.user) return;
    map.easeTo({ center: [props.user.lng, props.user.lat], duration: 600 });
  }, [props.follow, props.user]);

  useEffect(() => {
    const focus = props.focus;
    if (!focus || focus.nonce === 0) return;
    const map = mapRef.current;
    if (!map || !readyRef.current) {
      pendingFocusRef.current = { lat: focus.lat, lng: focus.lng };
      return;
    }
    map.easeTo({ center: [focus.lng, focus.lat], zoom: Math.max(map.getZoom(), 15), duration: 600 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.focus?.nonce]);

  useEffect(() => {
    const map = mapRef.current;
    const gl = glRef.current;
    if (!map || !gl || props.recenterNonce === 0) return;
    if (props.user) {
      map.easeTo({ center: [props.user.lng, props.user.lat], zoom: Math.max(map.getZoom(), 15) });
      return;
    }
    const coordinates: Array<[number, number]> = [
      ...(props.places ?? []).map((place) => [place.lng, place.lat] as [number, number]),
      ...(props.presence ?? []).map((live) => [live.lng, live.lat] as [number, number]),
    ].filter(insideIndonesia);
    if (coordinates.length === 0) {
      map.fitBounds(INDONESIA_BOUNDS as never, { padding: 24, duration: 600 });
      return;
    }
    const bounds = new gl.LngLatBounds(coordinates[0]!, coordinates[0]!);
    for (const coordinate of coordinates) bounds.extend(coordinate);
    map.fitBounds(bounds, { padding: 48, maxZoom: 16, duration: 600 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.recenterNonce]);

  return (
    <div className="map-canvas-wrap">
      <div className="map-canvas" ref={containerRef} />
      {placeClusters.map((cluster) => {
        const element = placeMarkersRef.current.get(cluster.id)?.element;
        if (!element) return null;
        const places = cluster.members.map((member) => member.item);
        const { total } = travellersOfPlaces(places);
        const name = places.length === 1 ? places[0]!.placeName : null;
        return createPortal(
          <PlaceMarker
            cluster={cluster}
            selfUserId={props.selfUserId ?? null}
            label={`${name ?? labels.place}: ${total}`}
            onOpen={(selected) => props.onSelectPlaces?.(selected)}
          />,
          element,
          cluster.id,
        );
      })}
      {presenceClusters.map((cluster) => {
        const element = presenceMarkersRef.current.get(cluster.id)?.element;
        if (!element) return null;
        return createPortal(
          <PresenceMarker
            cluster={cluster}
            selfUserId={props.selfUserId ?? null}
            selfLabel={labels.self}
            groupLabel={labels.travellers}
            onOpen={(selected) => props.onSelectTravellers?.(selected)}
          />,
          element,
          cluster.id,
        );
      })}
    </div>
  );
}
